// PROA_TRIAL_30D_UNIFIED_V1
import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback, useRef } from 'react';
import { Session } from '@supabase/supabase-js';
import { isCancelledError, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../config/supabaseClient';
import { prepareOptimizationCache } from '../services/optimizationService';
import { clearUserBrowserData } from '../config/clearUserBrowserData';
import axiosInstance from '../config/axiosInstance';
import { UserProfile, Tier } from '../types'; 
import { clearPricingMemory, readPricingMemory, savePricingMemory } from '../modules/pricingJourney/pricingMemory';

type IndustrialCostsRole = 'admin' | 'factory_operator' | 'factory_manager' | null;

// IND-056: misma consulta que usan las pantallas de Control industrial, para
// que el contexto se pida una sola vez.
const INDUSTRIAL_CONTEXT_QUERY_KEY = ['industrial-cost-context'];

// IND-056/IND-058: en Control industrial y en el puesto de los operarios
// (/factory) no se usa nada de Pricing; su contexto se pide al entrar en
// cualquier otra parte de la app (ensurePricingContext).
export function isPricingFreePath(pathname: string): boolean {
  return pathname.startsWith('/industrial') || pathname.startsWith('/factory');
}

function isIndustrialPath(): boolean {
  return isPricingFreePath(window.location.pathname);
}
type PricingRole = 'admin' | 'member' | null;
export interface PricingAccess {
  state: 'trial' | 'paid' | 'expired' | 'unavailable';
  has_access: boolean;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  trial_optimizations_used: number;
  trial_optimization_limit?: number;
}

// PRC-018: motivo del bloqueo y fechas que muestran las pantallas D1–D3.
export interface PricingBlock {
  reason: 'trial_ended' | 'no_plan' | 'subscription_canceled' | 'subscription_unpaid' | 'subscription_ended' | 'unavailable';
  plan: 'growth' | 'autonomy' | null;
  ended_at: string | null;
  data_retained_until: string | null;
  reactivate_in_portal: boolean;
}

export interface PricingAccountAdmin {
  full_name: string | null;
  email: string | null;
}

interface AuthContextType {
  session: Session | null;
  user: UserProfile | null;
  tier: Tier | null;
  pricingRole: PricingRole;
  pricingAccess: PricingAccess | null;
  hasResolvedPricingAccess: boolean;
  hasPricingAccess: boolean;
  pricingBlock: PricingBlock | null;
  pricingAccountName: string | null;
  pricingAccountAdmin: PricingAccountAdmin | null;
  // PRC-018: código para soporte si no se pudo cargar la cuenta; null si cargó.
  pricingContextError: string | null;
  // PRC-018: el usuario no pertenece a ninguna cuenta de Pricing (403).
  pricingAccountMissing: boolean;
  reloadPricingContext: (force?: boolean) => void;
  isPricingAdmin: boolean;
  industrialCostsRole: IndustrialCostsRole;
  hasResolvedIndustrialCostsAccess: boolean;
  isFactoryOperator: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  ensurePricingContext: () => void;
}

export const AuthContext = createContext<AuthContextType>(null!);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [tier, setTier] = useState<Tier | null>(null);
  const [pricingRole, setPricingRole] = useState<PricingRole>(null);
  const [pricingAccess, setPricingAccess] = useState<PricingAccess | null>(null);
  const [hasResolvedPricingAccess, setHasResolvedPricingAccess] = useState(false);
  const [pricingBlock, setPricingBlock] = useState<PricingBlock | null>(null);
  const [pricingAccountName, setPricingAccountName] = useState<string | null>(null);
  const [pricingAccountAdmin, setPricingAccountAdmin] = useState<PricingAccountAdmin | null>(null);
  const [pricingContextError, setPricingContextError] = useState<string | null>(null);
  const [pricingAccountMissing, setPricingAccountMissing] = useState(false);
  const [industrialCostsRole, setIndustrialCostsRole] = useState<IndustrialCostsRole>(null);
  const [hasResolvedIndustrialCostsAccess, setHasResolvedIndustrialCostsAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const authStateVersionRef = useRef(0);
  const activeSessionUserIdRef = useRef<string | null>(null);
  // IND-056: contexto de Pricing pendiente (se abrió la app en Control industrial).
  const deferredPricingRef = useRef<{ user: any; version: number; prepareCache: boolean } | null>(null);
  // PRC-026: versión de la sesión pintada con el estado recordado (null si no).
  const rememberedVersionRef = useRef<number | null>(null);
  const appQueryClient = useQueryClient();

  // Lógica de carga de datos (SEGUNDO PLANO)
  const fetchRichProfile = useCallback(async (
    currentSessionUser: any,
    expectedAuthStateVersion: number,
  ) => {
    const fallbackTier: Tier = {
      id: 'discovery',
      name: 'Discovery (Fallback)',
      price_monthly: 0,
      limit_sales_records: 1000,
      training_limit: 1,
      limit_optimizations_per_month: 10,
      training_period: 'monthly',
      training_period_days: 30,
      feature_explainability: true,
      feature_batch_optimization: false,
      feature_api_access: false,
      feature_ingest_mode: 'manual',
      feature_demand_level: 'hidden',
      feature_bi_export: false,
      feature_support_level: 'community',
    };

    const applyResolvedPricingContext = (
      profileData: any,
      tierData: Tier | null | undefined,
      resolvedPricingRole: PricingRole,
      access: PricingAccess | null,
    ) => {
      if (authStateVersionRef.current !== expectedAuthStateVersion) {
        return;
      }

      const finalTier = tierData ?? fallbackTier;

      setUser({
        ...currentSessionUser,
        ...(profileData ?? {}),
        tier_details: finalTier,
      });
      setTier(finalTier);
      setPricingRole(resolvedPricingRole);
      setPricingAccess(access);
      setHasResolvedPricingAccess(true);
    };

    const applyPricingExtras = (
      block: PricingBlock | null,
      accountName: string | null,
      accountAdmin: PricingAccountAdmin | null,
      errorCode: string | null,
      accountMissing: boolean,
    ) => {
      if (authStateVersionRef.current !== expectedAuthStateVersion) {
        return;
      }
      setPricingBlock(block);
      setPricingAccountName(accountName);
      setPricingAccountAdmin(accountAdmin);
      setPricingContextError(errorCode);
      setPricingAccountMissing(accountMissing);
    };

    try {
      const { data: pricingContext } = await axiosInstance.get(
        '/auth/pricing-app-context',
      );

      const resolvedPricingRole: PricingRole = (
        pricingContext?.pricing_role === 'admin' ||
        pricingContext?.pricing_role === 'member'
      )
        ? pricingContext.pricing_role
        : null;

      applyResolvedPricingContext(
        pricingContext?.profile ?? {},
        pricingContext?.tier as Tier | null | undefined,
        resolvedPricingRole,
        pricingContext?.access ?? null,
      );
      applyPricingExtras(
        pricingContext?.block ?? null,
        pricingContext?.account?.account_name ?? null,
        pricingContext?.account_admin ?? null,
        null,
        false,
      );
      // PRC-026: se recuerda solo el estado de la cuenta, y solo si hay acceso.
      if (authStateVersionRef.current === expectedAuthStateVersion && !resolvedPricingRole) clearPricingMemory();
      if (authStateVersionRef.current === expectedAuthStateVersion && resolvedPricingRole && pricingContext?.access) {
        const profile = pricingContext?.profile ?? {};
        savePricingMemory({
          userId: String(currentSessionUser?.id ?? ''),
          role: resolvedPricingRole,
          access: pricingContext.access,
          tier: (pricingContext?.tier as Tier | null) ?? null,
          profile: { tier_id: profile.tier_id ?? null, full_name: profile.full_name ?? null, company_name: profile.company_name ?? null },
          accountName: pricingContext?.account?.account_name ?? null,
        });
      }

      return;
    } catch (err: any) {
      if (authStateVersionRef.current !== expectedAuthStateVersion) {
        return;
      }

      if (err?.response?.status === 403) {
        clearPricingMemory();
        setUser(currentSessionUser as UserProfile);
        setTier(null);
        setPricingRole(null);
          setPricingAccess(null);
        applyPricingExtras(null, null, null, null, true);
        setHasResolvedPricingAccess(true);
        return;
      }

      // PRC-026: si la app ya se pintó con el estado recordado, un fallo de red
      // al confirmarlo no echa al usuario; el servidor sigue protegiendo los datos.
      if (rememberedVersionRef.current === expectedAuthStateVersion) {
        console.warn('Auth: no se pudo confirmar el estado recordado de Pricing.', err);
        return;
      }

      console.error(
        'Auth: No se pudo cargar pricing-app-context. Se bloquea el acceso Pricing hasta recuperar el contexto operativo.',
        err,
      );

      setUser(currentSessionUser as UserProfile);
      setTier(null);
      setPricingRole(null);
          setPricingAccess(null);
      applyPricingExtras(
        null, null, null,
        `CTX-${err?.response?.status ?? 'NET'}`,
        false,
      );
      setHasResolvedPricingAccess(true);
    }
  }, []);

  const fetchIndustrialCostsAccess = useCallback(async (
    expectedAuthStateVersion: number,
  ): Promise<IndustrialCostsRole> => {
    const fetchContext = () => appQueryClient.fetchQuery({
      queryKey: INDUSTRIAL_CONTEXT_QUERY_KEY,
      queryFn: async () => (await axiosInstance.get('/industrial-costs/context')).data,
      staleTime: 60_000,
      retry: false,
    });
    try {
      let data;
      try {
        data = await fetchContext();
      } catch (err) {
        // IND-058: si se vació la caché a mitad (p. ej. al entrar), se pide otra
        // vez en lugar de quedarse sin rol.
        if (!isCancelledError(err) || authStateVersionRef.current !== expectedAuthStateVersion) throw err;
        data = await fetchContext();
      }

      if (authStateVersionRef.current !== expectedAuthStateVersion) {
        return null;
      }

      const role = data?.role ?? null;

      if (
        role === 'admin' ||
        role === 'factory_operator' ||
        role === 'factory_manager'
      ) {
        setIndustrialCostsRole(role);
        return role;
      }

      setIndustrialCostsRole(null);
      return null;
    } catch (err) {
      if (authStateVersionRef.current !== expectedAuthStateVersion) {
        return null;
      }

      setIndustrialCostsRole(null);
      return null;
    } finally {
      if (authStateVersionRef.current === expectedAuthStateVersion) {
        setHasResolvedIndustrialCostsAccess(true);
      }
    }
  }, [appQueryClient]);

  // PRC-026: pinta la app al instante con el estado recordado del mismo usuario.
  const applyRememberedPricingContext = useCallback((currentSessionUser: any, version: number) => {
    const remembered = readPricingMemory(String(currentSessionUser?.id ?? ''));
    if (!remembered || authStateVersionRef.current !== version) {
      rememberedVersionRef.current = null;
      return;
    }
    rememberedVersionRef.current = version;
    setUser({
      ...currentSessionUser,
      ...remembered.profile,
      ...(remembered.tier ? { tier_details: remembered.tier } : {}),
    } as UserProfile);
    setTier(remembered.tier);
    setPricingRole(remembered.role);
    setPricingAccess(remembered.access);
    setPricingAccountName(remembered.accountName);
    setPricingBlock(null);
    setPricingAccountAdmin(null);
    setPricingContextError(null);
    setPricingAccountMissing(false);
    setHasResolvedPricingAccess(true);
  }, []);

  const loadPricingContext = useCallback((currentSessionUser: any, version: number, prepareCache: boolean) => {
    if (isIndustrialPath()) {
      deferredPricingRef.current = { user: currentSessionUser, version, prepareCache };
      return;
    }
    deferredPricingRef.current = null;
    applyRememberedPricingContext(currentSessionUser, version);
    fetchRichProfile(currentSessionUser, version);
    if (prepareCache) prepareOptimizationCache().catch(console.warn);
  }, [fetchRichProfile, applyRememberedPricingContext]);

  const ensurePricingContext = useCallback(() => {
    const deferred = deferredPricingRef.current;
    if (!deferred) return;
    deferredPricingRef.current = null;
    if (deferred.version !== authStateVersionRef.current) return;
    applyRememberedPricingContext(deferred.user, deferred.version);
    fetchRichProfile(deferred.user, deferred.version);
    if (deferred.prepareCache) prepareOptimizationCache().catch(console.warn);
  }, [fetchRichProfile, applyRememberedPricingContext]);

  // PRC-018: «Reintentar» y reacción a PROAPRICING_SUBSCRIPTION_REQUIRED a mitad de sesión.
  const reloadPricingContext = useCallback((force = true) => {
    if (!session?.user || isPricingFreePath(window.location.pathname)) return;
    // Ante el aviso del servidor, si ya sabemos que no hay acceso no se recarga (evita bucles).
    if (!force && pricingAccess?.has_access === false) return;
    const version = authStateVersionRef.current;
    rememberedVersionRef.current = null;
    setPricingContextError(null);
    setHasResolvedPricingAccess(false);
    fetchRichProfile(session.user, version);
  }, [session, fetchRichProfile, pricingAccess]);

  useEffect(() => {
    // PRC-026: el servidor dice que falta suscripción: se olvida lo recordado.
    const onAccessRequired = () => { clearPricingMemory(); reloadPricingContext(false); };
    window.addEventListener('pricing-access-required', onAccessRequired);
    return () => window.removeEventListener('pricing-access-required', onAccessRequired);
  }, [reloadPricingContext]);

  const refreshProfile = useCallback(async () => {
    if (session?.user) {
      const authStateVersion = authStateVersionRef.current;
      await fetchIndustrialCostsAccess(authStateVersion);
      await fetchRichProfile(session.user, authStateVersion);
    }
  }, [session, fetchRichProfile, fetchIndustrialCostsAccess]);

  useEffect(() => {
    let mounted = true;

    // --- INICIALIZACIÓN RÁPIDA (ESTILO ANTIGUO) ---
    const initSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!mounted) return;

        if (session) {
          // IND-056: si el aviso de inicio de sesión ya cargó esta sesión, no se repite.
          if (activeSessionUserIdRef.current === session.user.id) {
            setSession(session);
            setIsLoading(false);
            return;
          }
          activeSessionUserIdRef.current = session.user.id;
          authStateVersionRef.current += 1;
          const authStateVersion = authStateVersionRef.current;

          setSession(session);
          setUser(session.user as UserProfile);
          setHasResolvedPricingAccess(false);
          setHasResolvedIndustrialCostsAccess(false);

          fetchIndustrialCostsAccess(authStateVersion).catch(() => {
            if (authStateVersionRef.current === authStateVersion) {
              setIndustrialCostsRole(null);
              setHasResolvedIndustrialCostsAccess(true);
            }
          });

          loadPricingContext(session.user, authStateVersion, false);
        } else {
          authStateVersionRef.current += 1;
          setSession(null);
          setUser(null);
          setTier(null);
          setPricingRole(null);
          setPricingAccess(null);
          setPricingBlock(null); setPricingAccountName(null); setPricingAccountAdmin(null); setPricingContextError(null); setPricingAccountMissing(false);
          setHasResolvedPricingAccess(true);
          setIndustrialCostsRole(null);
          setHasResolvedIndustrialCostsAccess(true);
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Auth init error', error);

        if (mounted) {
          setSession(null);
          setUser(null);
          setTier(null);
          setPricingRole(null);
          setPricingAccess(null);
          setPricingBlock(null); setPricingAccountName(null); setPricingAccountAdmin(null); setPricingContextError(null); setPricingAccountMissing(false);
          setHasResolvedPricingAccess(true);
          setIndustrialCostsRole(null);
          setHasResolvedIndustrialCostsAccess(true);
          setIsLoading(false);
        }
      }
    };

    initSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('[Auth] Auth state changed:', event, newSession?.user?.id);

        if (event === 'SIGNED_IN' && newSession) {
          const previousUserId = activeSessionUserIdRef.current;
          const isSameUserSession = previousUserId === newSession.user.id;

          activeSessionUserIdRef.current = newSession.user.id;
          setSession(newSession);

          if (isSameUserSession) {
            setUser((currentUser) => (
              currentUser
                ? ({
                    ...currentUser,
                    ...newSession.user,
                    tier_details: currentUser.tier_details,
                  } as UserProfile)
                : (newSession.user as UserProfile)
            ));
            return;
          }

          authStateVersionRef.current += 1;
          const authStateVersion = authStateVersionRef.current;

          setUser(newSession.user as UserProfile);
          setHasResolvedPricingAccess(false);
          setHasResolvedIndustrialCostsAccess(false);
          // IND-058: ni los permisos del usuario anterior mientras llegan los nuevos.
          setTier(null);
          setPricingRole(null);
          setPricingAccess(null);
          setPricingBlock(null); setPricingAccountName(null); setPricingAccountAdmin(null); setPricingContextError(null); setPricingAccountMissing(false);
          setIndustrialCostsRole(null);
          // IND-058: otro usuario en la misma pestaña: nada del anterior.
          if (previousUserId) clearUserBrowserData();
          else appQueryClient.removeQueries({ queryKey: INDUSTRIAL_CONTEXT_QUERY_KEY });

          fetchIndustrialCostsAccess(authStateVersion).catch(() => {
            if (authStateVersionRef.current === authStateVersion) {
              setIndustrialCostsRole(null);
              setHasResolvedIndustrialCostsAccess(true);
            }
          });

          loadPricingContext(newSession.user, authStateVersion, true);

          setIsLoading(false);

          return;
        }

        if (event === 'SIGNED_OUT') {
          activeSessionUserIdRef.current = null;
          deferredPricingRef.current = null;
          authStateVersionRef.current += 1;
          setSession(null);
          setUser(null);
          setTier(null);
          setPricingRole(null);
          setPricingAccess(null);
          setPricingBlock(null); setPricingAccountName(null); setPricingAccountAdmin(null); setPricingContextError(null); setPricingAccountMissing(false);
          setHasResolvedPricingAccess(true);
          setIndustrialCostsRole(null);
          setHasResolvedIndustrialCostsAccess(true);
          // IND-058: consultas en memoria y lo guardado del usuario en el navegador.
          clearUserBrowserData();
          setIsLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchRichProfile, fetchIndustrialCostsAccess, loadPricingContext, appQueryClient]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    clearUserBrowserData();
  }, []);

  const value = {
    session,
    user,
    tier,
    pricingRole,
    pricingAccess,
    hasResolvedPricingAccess,
    hasPricingAccess: pricingRole !== null,
    pricingBlock,
    pricingAccountName,
    pricingAccountAdmin,
    pricingContextError,
    pricingAccountMissing,
    reloadPricingContext,
    isPricingAdmin: pricingRole === 'admin',
    industrialCostsRole,
    hasResolvedIndustrialCostsAccess,
    isFactoryOperator: industrialCostsRole === 'factory_operator',
    isLoading,
    isAuthenticated: !!session,
    signOut,
    refreshProfile,
    ensurePricingContext,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within a AuthProvider');
  return context;
};
